'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';

import Copy from '../../atoms/copy/copy';
// @ts-expect-error type
import MailIcon from '../../icons/mail.svg';
import styled from 'styled-components';

const ReverseTextInsideSpan = styled.span`
  unicode-bidi: bidi-override;
  direction: rtl;
`;

type EmailLinkProps = {
  encodedEmail: string;
  iconWidth?: number;
  iconHeight?: number;
} & React.ComponentProps<typeof Link>;

/*
 * #FR-2017:
 * Emails from the backend come as base64 encoded strings.
 * This component decodes that string on the client and
 * - reverses it. It then puts the reversed Text inside a Div that re-reverses it via css.
 * - only on hover/click/focus the email href will unobfuscate
 * */
export default function EmailLink({
  encodedEmail,
  iconWidth,
  iconHeight,
  ...props
}: EmailLinkProps) {
  const [hasInteracted, setHasInteracted] = useState(false);

  const { email, reversedEmail } = useMemo(() => {
    try {
      const decoded = atob(encodedEmail);
      const reversed = decoded.split('').reverse().join('');
      return { email: decoded, reversedEmail: reversed };
    } catch (e) {
      console.error('Failed to decode email address', e);
      return { email: '', reversedEmail: '' };
    }
  }, [encodedEmail]);

  const handleInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
    }
  };

  if (!email) return null;

  return (
    <Link
      href={hasInteracted ? `mailto:${email}` : '#'}
      onMouseEnter={handleInteraction}
      onFocus={handleInteraction}
      onClick={e => {
        handleInteraction();
        // Prevent routing to "#" if a user clicks before state updates
        if (!hasInteracted) e.preventDefault();
      }}
      {...props}
    >
      <MailIcon width={iconWidth || 33} height={iconHeight || 33} />
      <Copy tag="span" thick>
        {hasInteracted ? email : <ReverseTextInsideSpan>{reversedEmail}</ReverseTextInsideSpan>}
      </Copy>
    </Link>
  );
}
